import { isEqual } from 'lodash'
import { doesQualifyToBeHidden } from './ItemAvailabilityStatus'
import { getLocationBreakdown } from './LocationBreakdown'
import { getTermBreakdown } from './TermBreakdown'
import {
  getCountableMSServiceQuantity,
  getCountableMSServices,
  isSupersededMSService,
} from './MSServices'
import { getServicesForKitServiceFromProvidedServices } from './KitService'
import type {
  ILocationBreakdown,
  ITermBreakdown,
  TEnterableTerm,
  TMSService,
} from '~/types/msService/models'
import { isItemProduct } from '~/types/product/models'
import type { ITsd } from '~/types/tsd/models'
import {
  assertIsNumber,
  assertIsNumberAndIsGreaterThanZero,
} from '~/types/models'
import type { ISelectItem, TEntityId } from '~/types/models'
import * as KitQuantity from '~/cynqueModules/dataObjects/KitQuantity'
import { numberOrStringAsNumber } from '~/cynqueModules/utils/numberOrStringAsNumber'

const mn = '[cyqnueModules/dataObjects/KitItemServices]'

// The functions in this module are all used to populate a
// copy of msServices data for display in components generated
// using the msServices factory. They do not affect stored data,
// nor are they used to create data for storage.

/**
 * Where a kit's live items are: the same rows as its term
 * (getTermBreakdownFromKitItemServices), so nested kit rows, hidden and
 * superseded items do not count and `doInclude` is not required.
 *
 * Replaces a roll-up that gave a mixed kit the synthetic `{ id: 0, label:
 * 'Multi' }`, which no menu, title or lookup could do anything with.
 */
export function getLocationBreakdownFromKitItemServices({
  kitItemServices,
  kitLocationId,
}: {
  kitItemServices: TMSService[]
  /** The kit row's own stored location: wins a tie it is part of. */
  kitLocationId?: TEntityId | null
}): ILocationBreakdown {
  return getLocationBreakdown(
    kitItemServices.filter(
      (service: TMSService) =>
        isItemProduct(service.product) &&
        !doesQualifyToBeHidden(service) &&
        !isSupersededMSService(service)
    ),
    { preferredLocationId: kitLocationId }
  )
}
//
export function getSupplierFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const fn = `${mn} getSupplierFromKitItemServices |`

  const allTheSameSupplier = kitItemServices
    .filter(
      (service: TMSService) =>
        service.supplier &&
        !isEqual(service.supplier.label, '--') &&
        kitItemServices[0].supplier
    )
    .every(
      (service: TMSService) =>
        service.supplier?.id === kitItemServices[0].supplier?.id
    )
  console.debug(`${fn} allTheSameSupplier: ${allTheSameSupplier}`)

  return allTheSameSupplier
    ? kitItemServices[0].supplier
    : { id: 0, label: 'Varies' }
}
//
export function getSupplierAndTSDDescrFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const fn = `${mn} getSupplierAndTSDDescrFromKitItemServices |`

  const allFalseyTsd = kitItemServices.every(
    (service: TMSService) => !service.tsd
  )
  console.debug(`${fn} allFalseyTsd: ${allFalseyTsd}`)

  if (allFalseyTsd) {
    return getSupplierFromKitItemServices({ kitItemServices })?.label ?? 'None'
  }

  const allTheSameSupplier = kitItemServices
    .filter((service: TMSService) => !isEqual(service.supplier?.label, '--'))
    .every(
      (service: TMSService) =>
        service.supplier?.id === kitItemServices[0].supplier?.id
    )
  console.debug(`${mn} allTheSameSupplier: ${allTheSameSupplier}`)

  const allTheSameTsd = kitItemServices
    .filter((service: TMSService) => !isEqual(service.tsd?.label, '--'))
    .every(
      (service: TMSService) => service.tsd?.id === kitItemServices[0].tsd?.id
    )
  console.debug(`${mn} allTheSameTsd: ${allTheSameTsd}`)

  return allTheSameSupplier && allTheSameTsd
    ? `${kitItemServices[0].supplier?.label}\n${kitItemServices[0].tsd?.descr}`
    : allTheSameSupplier
    ? `${kitItemServices[0].supplier?.label}\nVaries`
    : allTheSameTsd
    ? `Varies\n${kitItemServices[0].tsd?.descr}`
    : 'VariesmVaries'
}
//
/**
 * A kit's term, counted over its live items: the same rows as a service
 * group's # Srvs set, except that `doInclude` is not required -- it is a
 * supplier choice made for the whole kit, and a kit from the supplier the
 * opportunity is not counting still has terms to show.
 */
export function getTermBreakdownFromKitItemServices({
  kitItemServices,
  masterServiceDefaultTerm,
}: {
  kitItemServices: TMSService[]
  /** The kit's service group's defaultTerm: wins a tie it is part of. */
  masterServiceDefaultTerm?: TEnterableTerm | null
}): ITermBreakdown {
  return getTermBreakdown(
    kitItemServices.filter(
      (service: TMSService) =>
        isItemProduct(service.product) &&
        !doesQualifyToBeHidden(service) &&
        !isSupersededMSService(service)
    ),
    { preferredTerm: masterServiceDefaultTerm }
  )
}
//
/**
 * The Term breakdown of a service group's # Srvs set (getCountableMSServices):
 * root kits and stand-alone items, each counted by its quantity, so the hover's
 * counts add up to the column.
 *
 * ⚠ A kit row stores no term, so a root kit counts under its ITEMS' majority
 * term -- what its own row shows -- or '--' with no live items.
 */
export function getCountableMSServicesTermBreakdown({
  msServices,
  preferredTerm,
}: {
  msServices: TMSService[]
  /** The service group's defaultTerm: wins a tie it is part of. */
  preferredTerm?: TEnterableTerm | null
}): ITermBreakdown {
  const termed = getCountableMSServices(msServices).map((msService) => ({
    term: isItemProduct(msService.product)
      ? msService.term
      : getTermBreakdownFromKitItemServices({
          kitItemServices: getServicesForKitServiceFromProvidedServices({
            providedServices: msServices,
            kitService: msService,
            doIncludeKitService: false,
            doIncludeDoesQualifyToBeHidden: false,
          }),
          masterServiceDefaultTerm: preferredTerm,
        }).majorityTerm,
    quantity: getCountableMSServiceQuantity(msService),
  }))
  return getTermBreakdown(termed, {
    preferredTerm,
    weightOf: (row) => row.quantity,
  })
}
//
/** `'Varies'` when the items differ -- see ITermBreakdown for who relies on it. */
export function getTermFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  return getTermBreakdownFromKitItemServices({ kitItemServices }).term
}
/**
 * The supplier whose TSDs Edit Kit's TSD field offers: the one the kit's live
 * items share, or `{ id: 0, label: 'Varies' }` when they differ -- including
 * when only some have a supplier -- which disables the field, since no single
 * supplier's TSDs are valid for every row (`null`, when what they share is
 * having none, disables it too). `kitSupplier` (the kit row's own) when the
 * kit has no live items.
 *
 * The items, not the kit row: they are what a TSD is spread onto. Not
 * getSupplierFromKitItemServices, which compares against the first row before
 * dropping the rows it ignores, so a first item with no supplier can make a
 * mixed kit read as uniform.
 */
export function getSupplierScopingKitTsd({
  kitSupplier,
  kitItemServices,
}: {
  kitSupplier: ISelectItem | null | undefined
  kitItemServices: TMSService[]
}): ISelectItem | null | undefined {
  const liveKitItemServices = kitItemServices.filter(
    (service) => !doesQualifyToBeHidden(service)
  )
  const [firstLiveKitItemService] = liveKitItemServices
  if (!firstLiveKitItemService) {
    return kitSupplier
  }
  const firstSupplierId = firstLiveKitItemService.supplier?.id
  const allTheSameSupplier = liveKitItemServices.every(
    (service) => service.supplier?.id === firstSupplierId
  )
  if (!allTheSameSupplier) {
    return { id: 0, label: 'Varies' }
  }
  return firstLiveKitItemService.supplier ?? null
}

/**
 * The TSD Edit Kit's TSD field opens on: the one the kit's live items share
 * (`null` when what they share is having none), or `{ id: 0, label: 'Varies' }`
 * when they differ. Left untouched it is not dirty, so not sent, and each item
 * keeps its own; changed, makeEditKitPreSaveProcessorOverride spreads it down
 * the kit. `undefined` when the kit has no live items -- the caller falls back
 * to the kit row's own `tsd`.
 *
 * Unlike the roll-ups below, hidden rows are dropped BEFORE picking the row to
 * compare against, so a hidden first item cannot make a uniform kit read
 * 'Varies'.
 */
export function getTsdFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}): ITsd | ISelectItem | null | undefined {
  const liveKitItemServices = kitItemServices.filter(
    (service) => !doesQualifyToBeHidden(service)
  )
  const [firstLiveKitItemService] = liveKitItemServices
  if (!firstLiveKitItemService) {
    return undefined
  }
  const firstTsd = firstLiveKitItemService.tsd ?? null
  const allTheSameTsd = liveKitItemServices.every(
    (service) => (service.tsd?.id ?? null) === (firstTsd?.id ?? null)
  )
  console.debug(`${mn} allTheSameTsd: ${allTheSameTsd}`)
  return allTheSameTsd ? firstTsd : { id: 0, label: 'Varies' }
}
//
export function getSplitFractionFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const allTheSameSplitFraction = kitItemServices
    .filter((service: TMSService) => !doesQualifyToBeHidden(service))
    .every(
      (service: TMSService) =>
        service.splitFraction === kitItemServices[0].splitFraction
    )
  console.debug(`${mn} allTheSameSplitFraction: ${allTheSameSplitFraction}`)
  return allTheSameSplitFraction ? kitItemServices[0].splitFraction : 'Varies'
}
//
export function getRecursFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const allTheSameRecurs = kitItemServices
    .filter((service: TMSService) => !doesQualifyToBeHidden(service))
    .every(
      (service: TMSService) =>
        service.recurs?.id === kitItemServices[0].recurs?.id
    )
  console.debug(`${mn} allTheSameRecurs: ${allTheSameRecurs}`)
  return allTheSameRecurs
    ? kitItemServices[0].recurs
    : { id: 999, label: 'Varies' }
}
//
export function getPaidFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const allTheSamePaid = kitItemServices
    .filter((service: TMSService) => !doesQualifyToBeHidden(service))
    .every(
      (service: TMSService) => service.paid?.id === kitItemServices[0].paid?.id
    )
  console.debug(`${mn} allTheSamePaid: ${allTheSamePaid}`)
  return allTheSamePaid ? kitItemServices[0].paid : { id: 999, label: 'Varies' }
}
//
export function getRecursPaidDescrFromKitItemServices({
  kitItemServices,
}: {
  kitItemServices: TMSService[]
}) {
  const firstOmssWithValidRecursPaidDescription = kitItemServices.find(
    (service: I) =>
      service.doInclude &&
      service.recursPaidDescr !== '--' &&
      !doesQualifyToBeHidden(service)
  )
  if (!firstOmssWithValidRecursPaidDescription) {
    return '--'
  }
  const allEqual = kitItemServices
    .filter(
      (service) =>
        service.doInclude &&
        service.recursPaidDescr !== '--' &&
        !doesQualifyToBeHidden(service)
    )
    .every((service) =>
      isEqual(
        service.recursPaidDescr,
        firstOmssWithValidRecursPaidDescription.recursPaidDescr
      )
    )
  return allEqual
    ? firstOmssWithValidRecursPaidDescription.recursPaidDescr
    : 'Varies'
}
//
export function getKitAmountTotalsFromKitItemServices({
  kitService,
  kitItemServices,
}: {
  kitService: TMSService
  kitItemServices: TMSService[]
}) {
  const fn = `${mn} getKitTotalsFromKitItemServices |`
  console.debug(`${mn}`)

  let unitRcAmount = 0.0
  let mrcAmount = 0.0
  let unitNrcAmount = 0.0
  let nrcAmount = 0.0
  let oneTimeAmount = 0.0
  let tcvAmount = 0.0
  let someRecursNotMonthly = false

  const quantity = kitService.quantity
  assertIsNumber(quantity, fn, 'quantity')

  kitItemServices.forEach((service: TMSService) => {
    const unitRcQtyForKitQty1 = quantity
      ? KitQuantity.getQuantityBasedOnKitQuantity({
          callingFn: fn,
          itemKitQuantity: service.kitQuantity ? service.kitQuantity : '0',
          kitMSServiceQuantity: quantity,
          itemDoesQualifyToBeHidden: doesQualifyToBeHidden(service),
        }) / quantity
      : 0
    console.debug(
      `${fn} service: ${
        service.product.descr
      }; unitRcQtyForKitQty1: ${unitRcQtyForKitQty1}; unitRcAmount: ${numberOrStringAsNumber(
        (service.unitRcAmount ?? 0) * unitRcQtyForKitQty1
      )} `
    )
    unitRcAmount +=
      numberOrStringAsNumber(service.unitRcAmount) * unitRcQtyForKitQty1
    mrcAmount += numberOrStringAsNumber(service.mrcAmount)
    unitNrcAmount +=
      numberOrStringAsNumber(service.unitNrcAmount) *
      (service.doMultiplyNrcByQuantity ? unitRcQtyForKitQty1 : 1)
    nrcAmount += numberOrStringAsNumber(service.nrcAmount)
    oneTimeAmount += numberOrStringAsNumber(service.oneTimeAmount)
    tcvAmount += numberOrStringAsNumber(service.tcvAmount)
    if (service.recurs && service.recurs?.id !== 1) {
      someRecursNotMonthly = true
    }
  })

  return {
    unitRcAmount: someRecursNotMonthly ? undefined : unitRcAmount,
    mrcAmount,
    unitNrcAmount,
    nrcAmount,
    oneTimeAmount,
    tcvAmount,
  }
}
